import React, { useMemo } from 'react';
import PriceBollingerChart, { formatVolume } from './PriceBollingerChart';
import { rangePositionScore } from '../shared/earningsSetup';
import { formatEventDate } from '../utils/earningsElapsed';
import { finiteOrNull } from '../utils/numbers';
import { TEXT_SUBTLE } from '../constants/uiColors';
import '../styles/TradingRangeInsights.css';

const analyzePriceAndVolume = (currentPrice, fiftyDayAvg, twoHundredDayAvg, currentVolume, avgVolume10D, avgVolume3M, technicalSignals) => {
  const priceChange = currentPrice - fiftyDayAvg;
  const shortTermVsLongTermPrice = fiftyDayAvg / twoHundredDayAvg;
  const currentVsShortTermVolume = currentVolume / avgVolume10D;
  const currentVsLongTermVolume = currentVolume / avgVolume3M;

  let insights = [];

  // Add technical signals analysis first
  if (technicalSignals?.length > 0) {
    const buySignals = technicalSignals.filter(s => s.type.toUpperCase() === 'BUY');
    const sellSignals = technicalSignals.filter(s => s.type.toUpperCase() === 'SELL');

    // Get strongest signals
    const strongestBuy = buySignals.reduce((prev, curr) =>
      (prev?.strength || 0) > curr.strength ? prev : curr, null);
    const strongestSell = sellSignals.reduce((prev, curr) =>
      (prev?.strength || 0) > curr.strength ? prev : curr, null);

    if (strongestBuy) {
      insights.push(`A ${strongestBuy.name} pattern has been detected with ${(strongestBuy.strength * 100).toFixed(0)}% strength, suggesting potential upward movement.`);
    }
    if (strongestSell) {
      insights.push(`A ${strongestSell.name} pattern has been detected with ${(strongestSell.strength * 100).toFixed(0)}% strength, indicating possible downward pressure.`);
    }
  }

  // Price trend analysis - integrate with technical signals
  if (shortTermVsLongTermPrice > 1.05) {
    const goldenCross = technicalSignals?.find(s => s.name === 'Golden Cross with Volume');
    if (goldenCross) {
      insights.push("The 50-day moving average is above the 200-day moving average, confirmed by a Golden Cross signal, strongly suggesting a bullish trend.");
    } else {
      insights.push("The 50-day moving average is above the 200-day moving average, indicating a potential bullish trend.");
    }
  } else if (shortTermVsLongTermPrice < 0.95) {
    const deathCross = technicalSignals?.find(s => s.name === 'Death Cross with Volume');
    if (deathCross) {
      insights.push("The 50-day moving average is below the 200-day moving average, confirmed by a Death Cross signal, strongly suggesting a bearish trend.");
    } else {
      insights.push("The 50-day moving average is below the 200-day moving average, indicating a potential bearish trend.");
    }
  }

  // Volume trend analysis - integrate with gap signals
  if (currentVsShortTermVolume > 1.5 && currentVsLongTermVolume > 1.5) {
    const gapUp = technicalSignals?.find(s => s.name === 'Gap Up Momentum');
    const gapDown = technicalSignals?.find(s => s.name === 'Gap Down Momentum');

    if (priceChange > 0) {
      if (gapUp) {
        insights.push("The stock is up on significantly higher volume, with a confirmed Gap Up Momentum signal indicating strong buying pressure.");
      } else {
        insights.push("The stock is up on significantly higher volume than recent averages, suggesting strong buying pressure.");
      }
    } else {
      if (gapDown) {
        insights.push("The stock is down on significantly higher volume, with a confirmed Gap Down Momentum signal indicating increased selling pressure.");
      } else {
        insights.push("The stock is down on significantly higher volume than recent averages, potentially indicating increased selling pressure.");
      }
    }
  }

  // Support/Resistance analysis
  const supportBounce = technicalSignals?.find(s => s.name === 'Support Bounce');
  const resistanceRejection = technicalSignals?.find(s => s.name === 'Resistance Rejection');

  if (supportBounce) {
    insights.push(`Price bounced off support level with ${(supportBounce.strength * 100).toFixed(0)}% confidence, suggesting potential reversal from downtrend.`);
  }
  if (resistanceRejection) {
    insights.push(`Price rejected at resistance level with ${(resistanceRejection.strength * 100).toFixed(0)}% confidence, indicating possible trend reversal.`);
  }

  // Conclusion - integrate all signals
  let conclusion = "";
  const hasPositiveSignals = technicalSignals?.some(s => s.type.toUpperCase() === 'BUY');
  const hasNegativeSignals = technicalSignals?.some(s => s.type.toUpperCase() === 'SELL');

  if (priceChange > 0 && hasPositiveSignals) {
    conclusion = "Multiple technical indicators and price action suggest growing investor interest and potential further upside.";
  } else if (priceChange < 0 && hasNegativeSignals) {
    conclusion = "Technical signals and price action indicate sustained selling pressure, suggesting caution.";
  } else if (priceChange > 0 && hasNegativeSignals) {
    conclusion = "Despite price increase, technical signals suggest potential resistance ahead.";
  } else if (priceChange < 0 && hasPositiveSignals) {
    conclusion = "While price is declining, technical signals suggest potential support levels nearby.";
  }

  return insights.join(" ") + (conclusion ? " " + conclusion : "");
};

const determineBadgeType = (fiftyDayAvg, twoHundredDayAvg, volumeCurrent, volumeAvg10D, technicalSignals) => {
  // First check for strong technical signals
  if (technicalSignals?.length > 0) {
    const strongestSignal = technicalSignals.reduce((prev, curr) =>
      (prev?.strength || 0) > curr.strength ? prev : curr, null);

    if (strongestSignal && strongestSignal.strength > 0.7) { // 70% threshold for strong signals
      if (strongestSignal.type.toUpperCase() === 'SELL') {
        if (strongestSignal.name.includes('Resistance')) return "Resistance Level";
        if (strongestSignal.name.includes('Death Cross')) return "Bearish Trend";
        return "Bearish Signal";
      } else {
        if (strongestSignal.name.includes('Support')) return "Support Level";
        if (strongestSignal.name.includes('Golden Cross')) return "Bullish Trend";
        return "Bullish Signal";
      }
    }
  }

  // If no strong technical signals, fall back to moving average analysis
  const shortTermVsLongTermPrice = fiftyDayAvg / twoHundredDayAvg;
  const currentVsShortTermVolume = volumeCurrent / volumeAvg10D;

  if (shortTermVsLongTermPrice > 1.05 && currentVsShortTermVolume > 1.2) {
    return "Bullish Trend";
  } else if (shortTermVsLongTermPrice < 0.95 && currentVsShortTermVolume > 1.2) {
    return "Bearish Trend";
  } else if (Math.abs(shortTermVsLongTermPrice - 1) <= 0.05) {
    return "Consolidating";
  }

  return "Neutral";
};

const getBadgeClassName = (type) => {
  switch (type) {
    case 'Bullish Trend':
    case 'Bullish Signal':
    case 'Support Level':
      return 'badge badge-bullish';
    case 'Bearish Trend':
    case 'Bearish Signal':
    case 'Resistance Level':
      return 'badge badge-bearish';
    case 'Consolidating':
      return 'badge badge-consolidating';
    default:
      return 'badge badge-neutral';
  }
};

// "42% below 10D avg (14.8M)" fragments for the volume summary line
const volumeVsAvg = (current, avg, label) => {
  if (isNaN(avg) || avg <= 0) return null;
  const pct = (current / avg - 1) * 100;
  const dir = pct >= 0 ? 'above' : 'below';
  return `${Math.abs(pct).toFixed(0)}% ${dir} ${label} (${formatVolume(avg)})`;
};

// 0–1 signal strength → whole percent; absence stays null.
const strengthPercent = (raw) => {
  const value = finiteOrNull(raw);
  return value === null ? null : Math.round(value * 100);
};

// The table's Signals column as a list (issue #445): which signals fired, their
// direction, strength and session. Newest session first, strongest first within
// it. `date` is the signal's bar date ('YYYY-MM-DD'), read by prefix.
const signalsForDisplay = (signals) => {
  if (!Array.isArray(signals)) return [];
  return signals
    .filter((signal) => signal && typeof signal.name === 'string' && typeof signal.type === 'string')
    .map((signal) => ({
      name: signal.name,
      isBuy: signal.type.toUpperCase() === 'BUY',
      strength: strengthPercent(signal.strength),
      date: typeof signal.date === 'string' ? signal.date : '',
    }))
    .sort((a, b) => b.date.localeCompare(a.date) || (b.strength ?? -1) - (a.strength ?? -1));
};

const SignalList = ({ signals }) => (
  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
    {signals.map((signal) => (
      <li
        key={`${signal.name}-${signal.date}`}
        style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: '0.875rem' }}
      >
        <span
          style={{
            flexShrink: 0,
            minWidth: 34,
            textAlign: 'center',
            padding: '0 6px',
            borderRadius: 4,
            fontSize: '0.7rem',
            fontWeight: 700,
            color: signal.isBuy ? '#15803d' : '#b91c1c',
            background: signal.isBuy ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
          }}
        >
          {signal.isBuy ? 'BUY' : 'SELL'}
        </span>
        <span style={{ flex: 1, color: '#1f2937' }}>{signal.name}</span>
        <span style={{ color: TEXT_SUBTLE, whiteSpace: 'nowrap' }}>
          {signal.strength !== null && `${signal.strength}%`}
          {signal.strength !== null && formatEventDate(signal.date) && ' · '}
          {formatEventDate(signal.date)}
        </span>
      </li>
    ))}
  </ul>
);

// One pill in the Performance row: the period moves and the 52W range position.
const PILL_STYLE = {
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: '4px',
  padding: '2px 8px',
  borderRadius: '6px',
  fontSize: '12px',
};
const PILL_TONES = {
  up: { color: '#16a34a', backgroundColor: 'rgba(34,197,94,0.08)' },
  down: { color: '#dc2626', backgroundColor: 'rgba(239,68,68,0.08)' },
  flat: { color: TEXT_SUBTLE, backgroundColor: '#f3f4f6' },
  neutral: { color: '#374151', backgroundColor: '#f3f4f6' },
};

const Pill = ({ label, value, tone, title, style }) => (
  <span title={title} style={{ ...PILL_STYLE, backgroundColor: PILL_TONES[tone].backgroundColor, ...style }}>
    <span style={{ color: TEXT_SUBTLE }}>{label}</span>
    <span style={{ color: PILL_TONES[tone].color, fontWeight: 600 }}>{value}</span>
  </span>
);

const TradingRangeInsights = ({ stock }) => {
  // Extract necessary data from stock
  const {
    fiftyTwoWeekLow,
    fiftyTwoWeekHigh,
    stockPrice, // Current price (string)
    fiftyDayAverage,
    twoHundredDayAverage,
    regularMarketVolume,
    averageVolume3Month,
    averageVolume10Day,
    lastEarningsDate,
    earningsDate,
    isEarningsDateEstimated,
    earningsReactionPercent,
    // Price performance
    priceChange3d,
    priceChange7d,
    priceChange14d,
    priceChange1m,
    priceChange3m,
  } = stock;

  const priceChanges = [
    { label: '3D', value: priceChange3d },
    { label: '7D', value: priceChange7d },
    { label: '14D', value: priceChange14d },
    { label: '1M', value: priceChange1m },
    { label: '3M', value: priceChange3m },
  ].filter(p => p.value != null);

  // Convert string values to numbers
  const currentPrice = parseFloat(stockPrice);
  const priceMin = parseFloat(fiftyTwoWeekLow);
  const priceMax = parseFloat(fiftyTwoWeekHigh);
  const fiftyDayAvg = parseFloat(fiftyDayAverage);
  const twoHundredDayAvg = parseFloat(twoHundredDayAverage);
  const volumeCurrent = parseFloat(regularMarketVolume);
  const volumeAvg3M = parseFloat(averageVolume3Month);
  const volumeAvg10D = parseFloat(averageVolume10Day);

  const interpretation = useMemo(
    () => analyzePriceAndVolume(currentPrice, fiftyDayAvg, twoHundredDayAvg, volumeCurrent, volumeAvg10D, volumeAvg3M, stock.technicalSignals),
    [currentPrice, fiftyDayAvg, twoHundredDayAvg, volumeCurrent, volumeAvg10D, volumeAvg3M, stock.technicalSignals]
  );

  const badgeType = useMemo(
    () => determineBadgeType(fiftyDayAvg, twoHundredDayAvg, volumeCurrent, volumeAvg10D, stock.technicalSignals),
    [fiftyDayAvg, twoHundredDayAvg, volumeCurrent, volumeAvg10D, stock.technicalSignals]
  );

  // Where the price sits in its 52-week range (the table's 52W Range column),
  // through the same definition the Expectations Bar uses.
  const rangePosition = rangePositionScore(currentPrice, priceMin, priceMax);

  const signals = useMemo(() => signalsForDisplay(stock.technicalSignals), [stock.technicalSignals]);

  // Post-earnings reaction: close after vs close before the last report.
  // Computed by screen_query from ONE earnings_history row (issue #212). It
  // used to be derived here from two prices carried separately on
  // current_price_cache, which could describe different quarters — and did:
  // the pair was written only by a worker that had stopped running, while the
  // marker's date beside it stayed current.
  const lastEarningsGapPct = earningsReactionPercent ?? null;

  const volumeSummary = useMemo(() => {
    if (isNaN(volumeCurrent)) return null;
    const parts = [
      volumeVsAvg(volumeCurrent, volumeAvg10D, '10D avg'),
      volumeVsAvg(volumeCurrent, volumeAvg3M, '3M avg'),
    ].filter(Boolean);
    if (!parts.length) return null;
    return `${formatVolume(volumeCurrent)} today · ${parts.join(' · ')}`;
  }, [volumeCurrent, volumeAvg10D, volumeAvg3M]);

  // Component Render with CSS classes
  return (
    <div className="trading-range-container">
      <div className="graph-column">
        <h3 className="title">Trading Range Analysis</h3>

        <PriceBollingerChart
          symbol={stock.tickerSymbol}
          fiftyTwoWeekLow={priceMin}
          fiftyTwoWeekHigh={priceMax}
          lastEarningsDate={lastEarningsDate}
          nextEarningsDate={earningsDate}
          nextEarningsEstimated={isEarningsDateEstimated}
          lastEarningsGapPct={lastEarningsGapPct}
        />

        {volumeSummary && (
          <div style={{ fontSize: '12px', color: TEXT_SUBTLE, margin: '4px 0 10px' }}>
            <span style={{ fontWeight: 600, color: '#374151' }}>Volume:</span> {volumeSummary}
          </div>
        )}

        {(priceChanges.length > 0 || rangePosition !== null) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>Performance:</span>
            {priceChanges.map(({ label, value }) => (
              <Pill
                key={label}
                label={label}
                value={`${value > 0 ? '+' : ''}${value.toFixed(1)}%`}
                tone={value > 0 ? 'up' : value < 0 ? 'down' : 'flat'}
              />
            ))}
            {rangePosition !== null && (
              <Pill
                label="52W range"
                value={`${rangePosition.toFixed(0)}%`}
                tone="neutral"
                title="Where the price sits between its 52-week low (0%) and high (100%)"
                style={{ marginLeft: priceChanges.length > 0 ? '6px' : 0 }}
              />
            )}
          </div>
        )}
      </div>

      <div className="insight-column">
        <h3 className="title">Insights</h3>
        {badgeType && (
          <div className={getBadgeClassName(badgeType)}>
            {badgeType}
          </div>
        )}
        <div className="insight-box">
          <h4 className="subtitle">Price and Volume Analysis</h4>
          <p className={`insight-text ${!interpretation ? 'placeholder-text' : ''}`}>
            {interpretation || "Price and volume patterns are within normal ranges without significant implications."}
          </p>
        </div>

        {signals.length > 0 && (
          <div className="insight-box">
            <h4 className="subtitle">Technical Signals</h4>
            <SignalList signals={signals} />
          </div>
        )}

      </div>
    </div>
  );
};

export default TradingRangeInsights;
